"use client";

import type { MagnusUIMessage } from "@magnus/contract/messages";
import { type EngineStatus, isBusyStatus as isBusy, settleDelegatedTurn } from "@workspace/voice-shared/live-call";
import { chatRelayNotes, collectTypedExchanges } from "@workspace/voice-shared/live-context";
import type { MagnusChatDelegate, MagnusChatOutcome } from "@workspace/voice-shared/magnus-chat-tool";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLatest } from "../use-latest";

/** One mounted chat a live call can delegate `magnus_chat` turns into. */
export type LiveCallChatBridge = {
  chatId: string;
  delegate: MagnusChatDelegate;
  stopTurn: () => void;
  whenIdle: () => Promise<void>;
  upsertRecap: (message: MagnusUIMessage) => void;
  removeRecap: (messageId: string) => void;
};

/** Sends a delegated turn as a user message; false when the chat refused it.
 *  Must resolve once the turn is sent, not when its reply finishes: the
 *  bridge watches the reply stream from then on. */
export type LiveCallChatSubmit<M extends MagnusUIMessage = MagnusUIMessage> = (parts: M["parts"], opts: {
  messageId: string;
  voiceSessionId?: string;
  voiceCallContext?: string;
}) => Promise<boolean>;

/**
 * A `LiveCallChatSubmit` result for an engine `sendMessage`, which settles
 * only when the reply does: false if it rejects at once (the chat refused
 * the turn), true otherwise. A failure mid-reply reaches the bridge as the
 * engine's `error` status instead.
 */
export function sendAccepted(sending: Promise<unknown>): Promise<boolean> {
  return Promise.race([sending.then(() => true, () => false), new Promise<boolean>(resolve => setTimeout(() => resolve(true), 0))]);
}

/** The chat as the bridge reads it, always the latest render's values. */
export type LiveCallChatInputs<M extends MagnusUIMessage> = {
  status: EngineStatus;
  messages: M[];
  submit: LiveCallChatSubmit<M>;
  stop: () => void;
  upsertRecap: (message: M) => void;
  removeRecap: (messageId: string) => void;
  sessionId: string | null;
  newId: () => string;
};

/**
 * The bridge over a chat that `latest` reads and `onChange` reports each
 * update of. A delegated query waits for the chat to go idle, is sent as a
 * user turn tagged with the call's session, and settles when its reply does.
 */
export function createLiveCallChatBridge<M extends MagnusUIMessage>(chatId: string, latest: () => LiveCallChatInputs<M>, onChange: (listener: () => void) => () => void): LiveCallChatBridge {
  const whenIdle = () => new Promise<void>(resolve => {
    if (!isBusy(latest().status)) {
      resolve();
      return;
    }
    const off = onChange(() => {
      if (!isBusy(latest().status)) {
        off();
        resolve();
      }
    });
  });
  return {
    chatId,
    whenIdle,
    stopTurn: () => latest().stop(),
    upsertRecap: message => latest().upsertRecap(message as M),
    removeRecap: messageId => latest().removeRecap(messageId),
    delegate: async (query, signal, callContext) => {
      await Promise.race([whenIdle(), new Promise<void>(resolve => signal.addEventListener("abort", () => resolve(), {
        once: true
      }))]);
      if (signal.aborted) {
        return {
          kind: "failed",
          message: "cancelled"
        };
      }
      const {
        newId,
        sessionId,
        submit
      } = latest();
      const userMessageId = newId();
      const sent = await submit([{
        type: "text",
        text: query
      }] as M["parts"], {
        messageId: userMessageId,
        ...(sessionId ? {
          voiceSessionId: sessionId
        } : {}),
        ...(callContext ? {
          voiceCallContext: callContext
        } : {})
      });
      if (!sent) {
        return {
          kind: "failed",
          message: "the chat couldn't take it",
          retryable: true
        };
      }
      let sawBusy = false;
      return new Promise<MagnusChatOutcome>(resolve => {
        const check = () => {
          const {
            messages,
            status
          } = latest();
          if (isBusy(status)) {
            sawBusy = true;
          }
          const outcome = settleDelegatedTurn(messages, status, userMessageId, sawBusy);
          if (outcome) {
            off();
            resolve(outcome);
          }
        };
        const off = onChange(check);
        signal.addEventListener("abort", () => {
          off();
          resolve({
            kind: "failed",
            message: "cancelled"
          });
        }, {
          once: true
        });
        check();
      });
    }
  };
}

/**
 * Turns the user types during a call, as notes for the voice model. A new
 * session starts from what the chat already holds, so only later turns are
 * relayed, each once its reply is in (or the chat goes idle).
 */
export function createTypedRelay(): (sessionId: string, messages: readonly MagnusUIMessage[], status: EngineStatus) => string[][] {
  let relay: {
    sessionId: string | null;
    seen: Set<string>;
  } = {
    sessionId: null,
    seen: new Set()
  };
  return (sessionId, messages, status) => {
    if (relay.sessionId !== sessionId) {
      relay = {
        sessionId,
        seen: new Set(messages.filter(m => m.role === "user").map(m => m.id))
      };
      return [];
    }
    return collectTypedExchanges(messages, relay.seen, !isBusy(status)).map(exchange => chatRelayNotes(exchange));
  };
}

/**
 * Binds a mounted chat engine to the live call: delegated `magnus_chat`
 * queries become user turns tagged with the call's `voiceSessionId`, and
 * turns the user types while on the call are relayed to the voice model.
 * Returns the bridge for the caller to register with the call controller.
 */
export function useLiveCallChatBridge<M extends MagnusUIMessage>({
  chatId,
  status,
  messages,
  submit,
  stop,
  upsertRecap,
  removeRecap,
  call,
  newId = () => crypto.randomUUID()
}: {
  chatId: string;
  status: EngineStatus;
  messages: M[];
  submit: LiveCallChatSubmit<M>;
  stop: () => void;
  upsertRecap: (message: M) => void;
  removeRecap: (messageId: string) => void;
  /** The call while it is bound to this chat; null otherwise. */
  call: {
    sessionId: string | null;
    pushContext: (notes: readonly string[]) => void;
  } | null;
  /** Mints delegated message ids; default `crypto.randomUUID`. */
  newId?: () => string;
}): LiveCallChatBridge {
  const sessionId = call?.sessionId ?? null;
  const inputsRef = useLatest<LiveCallChatInputs<M>>({
    status,
    messages,
    submit,
    stop,
    upsertRecap,
    removeRecap,
    sessionId,
    newId
  });
  const listenersRef = useRef(new Set<() => void>());
  useEffect(() => {
    for (const listener of listenersRef.current) {
      listener();
    }
  });
  const bridge = useMemo(() => createLiveCallChatBridge(chatId, () => inputsRef.current, listener_0 => {
    listenersRef.current.add(listener_0);
    return () => listenersRef.current.delete(listener_0);
  }), [chatId, inputsRef]);
  const [relay] = useState(createTypedRelay);
  const pushContext = call?.pushContext;
  useEffect(() => {
    if (!(sessionId && pushContext)) {
      return;
    }
    for (const notes of relay(sessionId, messages, status)) {
      pushContext(notes);
    }
  }, [relay, sessionId, pushContext, messages, status]);
  return bridge;
}